import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One document generation at a time, and results of a stale request (the dialog closed, its
 * scope or the business changed) are ignored. Used by the document and packing-slip dialogs.
 * `resetKeys` start a fresh state (e.g. [visible, scope, businessId]).
 */
export function useGuardedGenerate<T>(resetKeys: unknown[], fallbackError: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<T | null>(null);
  const generation = useRef(0);
  const generating = useRef(false);

  useEffect(() => {
    generation.current++;
    generating.current = false;
    setBusy(false);
    setResult(null);
    setError(null);
    return () => { generation.current++; generating.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetKeys);

  const run = useCallback(async (produce: () => Promise<T>) => {
    if (generating.current) return;
    generating.current = true;
    const request = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      const value = await produce();
      if (request === generation.current) setResult(value);
    } catch (e: any) {
      if (request === generation.current) setError(e?.message || fallbackError);
    } finally {
      if (request === generation.current) { generating.current = false; setBusy(false); }
    }
  }, [fallbackError]);

  return { busy, error, setError, result, setResult, run };
}
